import {
  Box,
  Button,
  Center,
  CopyButton,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconMovieOff,
  IconReload,
} from "@tabler/icons-react";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";

type VideoItem = {
  url: string;
  name?: string;
  createdDate?: string;
};

/**
 * API chỉ trả về đường dẫn video nên chưa chốt được kiểu dữ liệu:
 * có thể là 1 chuỗi, mảng chuỗi hoặc mảng object. Gom hết về 1 dạng
 * để phần hiển thị bên dưới không phải quan tâm.
 */
const normalizeVideos = (raw: any): VideoItem[] => {
  if (!raw) return [];

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.urls)
      ? raw.urls
      : Array.isArray(raw?.data)
        ? raw.data
        : [raw];

  return list
    .map((item: any) => {
      if (typeof item === "string") return { url: item.trim() };
      if (!item || typeof item !== "object") return { url: "" };

      return {
        url: (
          item.url ??
          item.videoUrl ??
          item.urlVideo ??
          item.fileUrl ??
          item.link ??
          item.path ??
          ""
        )
          ?.toString()
          .trim(),
        name: item.fileName ?? item.name ?? item.title,
        createdDate:
          item.createdDate ?? item.createDate ?? item.recordDate ?? item.time,
      };
    })
    .filter((item: VideoItem) => item.url);
};

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "";
};

const OrderVideoModal = ({ orderNumber }: { orderNumber: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [videos, setVideos] = useState<VideoItem[]>([]);

  /**
   * Đơn chưa quay video và API trả về lỗi/không có data đều dẫn tới cùng
   * một kết quả với người dùng: không có gì để xem. Không tách ra thành
   * thông báo lỗi để tránh làm nhân viên tưởng hệ thống đang hỏng.
   */
  const fetchVideos = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/OrderVideo/get-url-video?OrderNumber=${encodeURIComponent(orderNumber)}`,
        true,
      );

      setVideos(response?.success ? normalizeVideos(response.data) : []);
    } catch (error) {
      console.error(error);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  if (isLoading) {
    return (
      <Center mih={200}>
        <Stack align="center" gap="xs">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Đang tải video giao hàng...
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md" py="xs">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Đơn hàng <b>{orderNumber}</b>
          {videos.length > 0 ? ` - ${videos.length} video` : ""}
        </Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconReload size={16} />}
          onClick={fetchVideos}
        >
          Tải lại
        </Button>
      </Group>

      {videos.length === 0 && (
        <Center mih={160}>
          <Stack align="center" gap="xs">
            <IconMovieOff
              size={40}
              stroke={1.5}
              color="var(--mantine-color-gray-5)"
            />
            <Text size="sm" c="dimmed">
              Không tìm thấy video giao hàng của đơn này.
            </Text>
          </Stack>
        </Center>
      )}

      {videos.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {videos.map((video, index) => (
            <Paper key={`${video.url}-${index}`} withBorder radius="md" p="sm">
              <Stack gap="xs">
                <Box
                  style={{
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <video
                    src={video.url}
                    controls
                    preload="metadata"
                    style={{ width: "100%", maxHeight: 340, display: "block" }}
                  />
                </Box>

                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>
                      {video.name ?? `Video ${index + 1}`}
                    </Text>
                    {formatDate(video.createdDate) && (
                      <Text size="xs" c="dimmed">
                        {formatDate(video.createdDate)}
                      </Text>
                    )}
                  </Box>

                  <Group gap={6} wrap="nowrap">
                    <CopyButton value={video.url} timeout={1500}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Đã sao chép" : "Sao chép đường dẫn"}
                        >
                          <Button
                            size="xs"
                            variant="light"
                            color={copied ? "teal" : "gray"}
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck size={16} />
                            ) : (
                              <IconCopy size={16} />
                            )}
                          </Button>
                        </Tooltip>
                      )}
                    </CopyButton>

                    <Tooltip label="Mở ở tab mới">
                      <Button
                        size="xs"
                        variant="light"
                        component="a"
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconExternalLink size={16} />
                      </Button>
                    </Tooltip>
                  </Group>
                </Group>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
};

export default OrderVideoModal;
